import React, { useState } from "react";
import {
  Database, Upload, CheckCircle2, ChevronRight,
  Table2, Columns3, AlertCircle, Info,
  RefreshCw, Sparkles, Eye, Check, X
} from "lucide-react";
import "./DatabaseSetupPage.css";

const STEPS = [
  { id: 1, label: "Upload Sample File",  desc: "Upload a CSV to detect schema" },
  { id: 2, label: "Review Columns",      desc: "Select columns to include"     },
  { id: 3, label: "Configure Table",     desc: "Set table name and options"    },
  { id: 4, label: "Create Table",        desc: "Deploy schema to RDS"          },
];

export default function DatabaseSetupPage({ idToken, setActiveTab, onTabChange }) {
  const [step, setStep]           = useState(1);
  const [dragging, setDragging]   = useState(false);
  const [fileName, setFileName]   = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [columns, setColumns]     = useState([]);
  const [tableName, setTableName] = useState("customers");
  const [schema, setSchema]       = useState("public");
  const [creating, setCreating]   = useState(false);
  const [created, setCreated]     = useState(false);
  const [aiPowered, setAiPowered] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [error, setError]         = useState(null);
  const [s3KeySample, setS3KeySample] = useState(null);

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setDetecting(true);
    setError(null);
    // Bulletproof: Strip accidental quotes, spaces, and trailing slashes from the .env variable
    const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

    try {
      // 1. Get pre-signed URL to upload the sample
      const presignedRes = await fetch(`${API_URL}/schema/sample-presigned-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ file_name: file.name })
      });
      if (!presignedRes.ok) {
        const errData = await presignedRes.json().catch(() => ({}));
        throw new Error(errData.detail || errData.message || "Failed to generate secure upload URL.");
      }
      const presignedData = await presignedRes.json();

      // 2. Upload the sample directly to Amazon S3
      await fetch(presignedData.upload_url, { method: "PUT", headers: { "Content-Type": "text/csv" }, body: file });
      setS3KeySample(presignedData.s3_key);

      // 3. Ask FastAPI & Amazon Bedrock to detect the schema
      const detectRes = await fetch(`${API_URL}/schema/detect?s3_key=${encodeURIComponent(presignedData.s3_key)}&file_name=${encodeURIComponent(file.name)}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${idToken}` }
      });
      if (!detectRes.ok) {
        const errData = await detectRes.json().catch(() => ({}));
        throw new Error(errData.detail || errData.message || "Failed to detect schema from AI backend.");
      }
      const detectData = await detectRes.json();

      // 4. Map the AI results to the UI state
      setColumns(detectData.inferred_columns.map(c => ({
        name: c.original_name, mapped_name: c.mapped_name, type: c.data_type,
        nullable: true, sample: c.sample_value || "", selected: c.include, pk: c.is_pk
      })));
      
      setAiPowered(detectData.ai_powered);
      if (detectData.message) setAiMessage(detectData.message);
      setDetecting(false);
      setStep(2);
    } catch (err) {
      console.error(err);
      setError(err.message);
      setDetecting(false);
    }
  };

  const toggleColumn = (idx) => {
    setColumns((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, selected: !c.selected } : c))
    );
  };

  const toggleAll = () => {
    const allSelected = columns.every((c) => c.selected);
    setColumns((prev) => prev.map((c) => ({ ...c, selected: !allSelected })));
  };

  const togglePk = (idx) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, pk: !c.pk } : c)));
  };

  const selectedCount = columns.filter((c) => c.selected).length;

  const handleCreateTable = async () => {
    setCreating(true);
    setError(null);
    // Bulletproof: Strip accidental quotes, spaces, and trailing slashes from the .env variable
    const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
    try {
      const payload = {
        file_name: fileName,
        s3_key_sample: s3KeySample,
        target_table_name: tableName,
        columns: columns.map(c => ({
          original_name: c.name, mapped_name: c.mapped_name, data_type: c.type,
          include: c.selected, is_pk: c.pk, standardization_rules: null
        }))
      };

      const res = await fetch(`${API_URL}/schema/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || errData.message || "Failed to create PostgreSQL table.");
      }

      const data = await res.json();
      setSchema(data.postgres_schema_name);
      setTableName(data.table_name);
      setCreated(true);
      setStep(4);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="dbsetup-root">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-icon page-icon-blue">
            <Database size={20} />
          </div>
          <div>
            <h1 className="page-title">Database Setup</h1>
            <p className="page-subtitle">
              Upload a sample file to detect schema, select columns, and create your RDS table.
            </p>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`step ${step === s.id ? "step-active" : ""} ${step > s.id ? "step-done" : ""}`}>
              <div className="step-circle">
                {step > s.id ? <Check size={14} /> : s.id}
              </div>
              <div className="step-text">
                <div className="step-label">{s.label}</div>
                <div className="step-desc">{s.desc}</div>
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`step-connector ${step > s.id ? "step-connector-done" : ""}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Global Error Display */}
      {error && (
        <div style={{ padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f87171' }}>
          <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
          {error}
        </div>
      )}

      {/* ── Step 1: Upload ─────────────────────────────────────── */}
      {step === 1 && (
        <div className="dbsetup-card animate-fade">
          <div className="card-header">
            <Upload size={18} />
            <h2 className="card-title">Upload Sample File</h2>
          </div>
          <p className="card-desc">
            Upload a CSV file with at least 5–10 rows. The AI will analyze the structure
            and suggest column types and names automatically.
          </p>

          <div
            className={`dropzone ${dragging ? "dropzone-active" : ""} ${detecting ? "dropzone-detecting" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("db-file-input").click()}
          >
            <input
              id="db-file-input"
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleDrop}
            />
            {detecting ? (
              <div className="dropzone-detecting-inner">
                <div className="detecting-spinner animate-spin" />
                <div className="detecting-text">
                  <span className="detecting-title">Analyzing schema…</span>
                  <span className="detecting-sub">Detecting column types and data patterns</span>
                </div>
              </div>
            ) : (
              <>
                <div className="dropzone-icon">
                  <Sparkles size={28} />
                </div>
                <div className="dropzone-text">
                  <span className="dropzone-title">
                    {fileName ? fileName : "Drop your CSV file here"}
                  </span>
                  <span className="dropzone-sub">
                    {fileName ? "Click to replace" : "or click to browse · CSV files only"}
                  </span>
                </div>
                <div className="dropzone-hint">
                  <Info size={13} />
                  AI will detect column names, data types, and suggest a schema
                </div>
              </>
            )}
          </div>

          <div className="info-box">
            <AlertCircle size={15} />
            <span>
              The sample file is used only for schema detection. Your actual data will be
              uploaded in the <strong>Data Upload</strong> step.
            </span>
          </div>
        </div>
      )}

      {/* ── Step 2: Column Review ──────────────────────────────── */}
      {step === 2 && (
        <div className="dbsetup-card animate-fade">
          <div className="card-header">
            <Columns3 size={18} />
            <h2 className="card-title">Review Detected Columns</h2>
            <div className="card-header-actions">
              <span className="badge badge-green">{selectedCount} selected</span>
              <button className="btn-ghost btn-sm" onClick={toggleAll}>
                {columns.every((c) => c.selected) ? "Deselect all" : "Select all"}
              </button>
            </div>
          </div>
          <p className="card-desc">
            The AI detected <strong>{columns.length} columns</strong> from <em>{fileName}</em>.
            Select the columns you want to include in the database table.
          </p>
      
      {/* AI Powered Badge */}
      {aiPowered && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe',
          color: '#5b21b6', padding: '10px 14px', borderRadius: '6px',
          marginBottom: '16px', fontSize: '14px', fontWeight: '500'
        }}>
          <Sparkles size={16} style={{ color: '#8b5cf6' }} />
          {aiMessage}
        </div>
      )}

          <div className="columns-table-wrap">
            <table className="columns-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="col-checkbox"
                      checked={columns.every((c) => c.selected)}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>Column Name</th>
                  <th>Detected Type</th>
                  <th>Sample Value</th>
                  <th>Nullable</th>
                  <th>PK</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((col, idx) => (
                  <tr
                    key={col.name}
                    className={`col-row ${col.selected ? "col-row-selected" : "col-row-dim"}`}
                    onClick={() => toggleColumn(idx)}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="col-checkbox"
                        checked={col.selected}
                        onChange={() => toggleColumn(idx)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <span className="col-name">{col.mapped_name} <br/><small style={{color: '#64748b'}}>from: {col.name}</small></span>
                    </td>
                    <td>
                      <span className="col-type">{col.type}</span>
                    </td>
                    <td>
                      <span className="col-sample text-muted">{col.sample || "—"}</span>
                    </td>
                    <td>
                      {col.nullable
                        ? <span className="badge badge-gray">NULL</span>
                        : <span className="badge badge-amber">NOT NULL</span>}
                    </td>
                    <td>
                      <button 
                        className={`badge ${col.pk ? 'badge-blue' : 'badge-gray'}`}
                        onClick={(e) => { e.stopPropagation(); togglePk(idx); }}
                        style={{ cursor: 'pointer', border: 'none', padding: '4px 8px' }}
                        title="Click to toggle Primary Key"
                      >
                        {col.pk ? "PK" : "Set PK"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-footer">
            <button className="btn-ghost" onClick={() => setStep(1)}>
              <RefreshCw size={14} /> Re-upload
            </button>
            <button
              className="btn-primary"
              onClick={() => setStep(3)}
              disabled={selectedCount === 0}
            >
              Continue <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Configure Table ────────────────────────────── */}
      {step === 3 && (
        <div className="dbsetup-card animate-fade">
          <div className="card-header">
            <Table2 size={18} />
            <h2 className="card-title">Configure Table</h2>
          </div>
          <p className="card-desc">
            Set the destination table name and schema. The table will be created in your
            connected Amazon RDS PostgreSQL instance.
          </p>

          <div className="config-grid">
            <div className="config-field">
              <label className="config-label">Schema</label>
              <input
                className="config-input"
                value={schema}
                onChange={(e) => setSchema(e.target.value)}
                placeholder="public"
              />
              <span className="config-hint">PostgreSQL schema (default: public)</span>
            </div>
            <div className="config-field">
              <label className="config-label">Table Name</label>
              <input
                className="config-input"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="customers"
              />
              <span className="config-hint">Lowercase, underscores only</span>
            </div>
          </div>

          {/* SQL Preview */}
          <div className="sql-preview">
            <div className="sql-preview-header">
              <Eye size={14} />
              <span>SQL Preview</span>
            </div>
            <pre className="sql-code">
{`CREATE TABLE ${schema}.${tableName} (
${columns
  .filter((c) => c.selected)
  .map((c) => `  ${c.name.padEnd(20)} ${c.type}${c.pk ? " PRIMARY KEY" : ""}${!c.nullable ? " NOT NULL" : ""}`)
  .join(",\n")}
);`}
            </pre>
          </div>

          <div className="card-footer">
            <button className="btn-ghost" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              className="btn-primary"
              onClick={handleCreateTable}
              disabled={!tableName || !schema || creating}
            >
              {creating ? (
                <><span className="btn-spinner animate-spin" /> Creating table…</>
              ) : (
                <>Create Table <Database size={15} /></>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Success ────────────────────────────────────── */}
      {step === 4 && (
        <div className="dbsetup-card animate-fade">
          <div className="success-banner">
            <div className="success-icon">
              <CheckCircle2 size={40} />
            </div>
            <h2 className="success-title">Table Created Successfully</h2>
            <p className="success-desc">
              <strong>{schema}.{tableName}</strong> has been created in your Amazon RDS
              PostgreSQL instance with {selectedCount} columns.
            </p>
          </div>

          <div className="success-details">
            {[
              { label: "Table",    value: `${schema}.${tableName}` },
              { label: "Columns",  value: `${selectedCount} columns` },
              { label: "Database", value: "datacatalog (RDS PostgreSQL)" },
              { label: "Status",   value: "Active", green: true },
            ].map((row) => (
              <div key={row.label} className="success-detail-row">
                <span className="success-detail-label">{row.label}</span>
                <span className={`success-detail-value ${row.green ? "text-green" : ""}`}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          <div className="card-footer card-footer-center">
            <button className="btn-ghost" onClick={() => { setStep(1); setFileName(null); setCreated(false); }}>
              <RefreshCw size={14} /> Set up another table
            </button>
        <button 
          className="btn-primary"
          onClick={() => {
            if (onTabChange) onTabChange("data-upload");
            else if (setActiveTab) setActiveTab("data-upload");
          }}
        >
              Go to Data Upload <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
